import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
  Divider,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  ExitToApp as LogoutIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import userService from '../services/userService';
import { useSnackbar } from 'notistack';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = authService.getCurrentUser();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await userService.getCurrentUser();
        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          password: '',
          confirmPassword: '',
        });
        setInitialLoading(false);
      } catch (error) {
        console.error('Error fetching user data:', error);
        // Fallback to localStorage data if backend call fails
        setFormData({
          name: currentUser.name || '',
          email: currentUser.email || '',
          password: '',
          confirmPassword: '',
        });
        setInitialLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogout = () => {
    authService.logout(navigate);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password && formData.password !== formData.confirmPassword) {
      enqueueSnackbar('Passwords do not match', { variant: 'error' });
      return;
    }

    setLoading(true);
    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        companyId: currentUser.companyId, // Required by DTO
      };

      if (formData.password) {
        updateData.password = formData.password;
      }

      await userService.updateUser(currentUser.id, updateData);
      
      // Update local storage with new details
      localStorage.setItem('userName', formData.name);
      localStorage.setItem('userEmail', formData.email);
      
      enqueueSnackbar('Profile updated successfully', { variant: 'success' });
      setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
    } catch (error) {
      console.error('Update profile error:', error);
      const message = error.response?.data?.message || 'Failed to update profile';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            color: 'text.secondary',
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            }
          }}
        >
          Go Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main', flexGrow: 1 }}>
          User Profile
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Left Column - Profile Overview */}
        <Grid item xs={12} md={4}>
          <Card 
            elevation={0} 
            sx={{ 
              textAlign: 'center', 
              py: 5, 
              px: 3,
              borderRadius: 4, 
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(145deg, #ffffff 0%, #f8faff 100%)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <CardContent>
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    bgcolor: 'primary.main',
                    fontSize: '3.5rem',
                    mx: 'auto',
                    boxShadow: '0 8px 24px rgba(25, 118, 210, 0.2)',
                    border: '4px solid #fff'
                  }}
                >
                  {formData.name.charAt(0).toUpperCase()}
                </Avatar>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                {formData.name}
              </Typography>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: 'primary.main', 
                  fontWeight: 600,
                  bgcolor: 'primary.lighter',
                  display: 'inline-block',
                  px: 2,
                  py: 0.5,
                  borderRadius: 5,
                  fontSize: '0.85rem',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  mb: 3
                }}
              >
                {currentUser.role}
              </Typography>
              
              <Box sx={{ textAlign: 'left', mb: 4 }}>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <EmailIcon sx={{ fontSize: 18, mr: 1.5, color: 'text.disabled' }} />
                  {formData.email}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ fontSize: 18, mr: 1.5, color: 'text.disabled' }} />
                  Member since {new Date().toLocaleDateString()}
                </Typography>
              </Box>

              <Divider sx={{ mb: 4 }} />
              
              <Button
                variant="contained"
                color="error"
                fullWidth
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{ 
                  borderRadius: 3, 
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(211, 47, 47, 0.2)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(211, 47, 47, 0.3)',
                  }
                }}
              >
                Logout Account
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Edit Form */}
        <Grid item xs={12} md={8}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 3, md: 5 }, 
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                Account Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Update your personal information and security credentials
              </Typography>
            </Box>
            
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, ml: 0.5 }}>Full Name</Typography>
                  <TextField
                    fullWidth
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    placeholder="Enter your full name"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: 'text.disabled' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, ml: 0.5 }}>Email Address</Typography>
                  <TextField
                    fullWidth
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    placeholder="yourname@company.com"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: 'text.disabled' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ py: 2 }}>
                    <Divider>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          px: 2, 
                          color: 'text.disabled', 
                          fontWeight: 700, 
                          textTransform: 'uppercase',
                          letterSpacing: 1
                        }}
                      >
                        Security & Password
                      </Typography>
                    </Divider>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, ml: 0.5 }}>New Password</Typography>
                  <TextField
                    fullWidth
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="Leave blank to keep current"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: 'text.disabled' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, ml: 0.5 }}>Confirm Password</Typography>
                  <TextField
                    fullWidth
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="Verify new password"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: 'text.disabled' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: 4 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    sx={{ 
                      py: 2, 
                      borderRadius: 3, 
                      fontWeight: 800,
                      fontSize: '1rem',
                      textTransform: 'none',
                      boxShadow: '0 8px 24px rgba(25, 118, 210, 0.25)',
                      '&:hover': {
                        boxShadow: '0 12px 32px rgba(25, 118, 210, 0.35)',
                      }
                    }}
                  >
                    {loading ? 'Saving Changes...' : 'Save Profile Changes'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProfilePage;
